import React, { useCallback, useEffect, useRef, useState } from 'react';

import i18n from '@nextroll/ar-i18n';
import { Link } from '@nextroll/ar-container-app';
import { InlineAlert } from '@nextroll/ar-style-base';
import { isAuthError } from 'util/util';

export interface INodeAuthSectionProps {
    advertisableEid?: string;
    hasAuthenticationGranted: boolean;
    updateAuthentications: () => void;
    missingScopes: boolean;
    validationError?: string;
    serviceTitle: string;
    entityTitle: string;
}

export const NodeAuthSection = (props: INodeAuthSectionProps) => {
    const {
        advertisableEid,
        hasAuthenticationGranted,
        updateAuthentications,
        missingScopes,
        validationError,
        serviceTitle,
        entityTitle,
    } = props;
    const invalidAuth = validationError && isAuthError(validationError);

    /**
     * Determine if the user is waiting for the service auth update.
     * This value is true after redirecting to the settings page and until the service auth is granted.
     */
    const [waitForAuth, setWaitForAuth] = useState(false);

    /**
     * Variable that marks the setInterval and prevent the page from throttling.
     */
    const heartbeatInterval = useRef<NodeJS.Timer>();

    /**
     * Start the auth check heartbeat while waiting for the auth update, if the canvas is visible.
     * If the user is watching the canvas, then we check the auth status regularly until the permission is granted.
     */
    const startHeartbeat = useCallback(() => {
        if (!waitForAuth) {
            return; // not waiting for auth to update; do nothing
        }
        heartbeatInterval.current && clearInterval(heartbeatInterval.current);
        heartbeatInterval.current = setInterval(updateAuthentications, 15000);
        updateAuthentications();
    }, [waitForAuth, updateAuthentications]);

    /**
     * Stop the auth check heartbeat when the canvas is not visible, to avoids unnecessary requests.
     */
    const stopHeartbeat = useCallback(() => heartbeatInterval.current && clearInterval(heartbeatInterval.current), []);

    /**
     * Register onFocus and onBlur listeners
     */
    useEffect(() => {
        window.addEventListener('focus', startHeartbeat);
        window.addEventListener('blur', stopHeartbeat);
        return () => {
            stopHeartbeat(); // make sure to stop any heartbeat request and then remove event listeners
            window.removeEventListener('focus', startHeartbeat);
            window.removeEventListener('blur', stopHeartbeat);
        };
    }, [startHeartbeat, stopHeartbeat]);

    /**
     * When the service auth is gained, make sure to stop the heartbeat.
     */
    useEffect(() => {
        if (hasAuthenticationGranted && (!missingScopes && !invalidAuth)) {
            setWaitForAuth(false);
            stopHeartbeat();
        }
    }, [hasAuthenticationGranted, missingScopes, stopHeartbeat, invalidAuth]);

    /**
     * This happens when the user hits the redirect button and goes to the settings page.
     * We set the waitForAuth value to true, and also start the initial heartbeat check.
     */
    const handleGoToSettings = useCallback(() => {
        setWaitForAuth(true);
        startHeartbeat();
    }, [startHeartbeat]);

    if (hasAuthenticationGranted && !missingScopes) {
        return null;
    }

    if (waitForAuth) {
        /**
         * The loading skeleton appears after the redirect button is pressed.
         * It keeps showing until the service auth is granted.
         */
        return (
            <>
                <div className='ar-scrolling-table-pill--loading' />
                <div className='ar-scrolling-table-pill--loading' />
                <div className='ar-scrolling-table-pill--loading' />
            </>
        );
    }

    if (invalidAuth) {
        return (
            <InlineAlert
                showIcon
                type={i18n.noop.gettext('error')}
                contentEl={(
                    <>
                        <span className='ar-alert-title' data-testid='workflow-auth-alert'>
                            {
                                i18n.gettext('Authentication error')
                            }
                        </span>
                        <span className='ar-alert-message'>
                            {
                                i18n.gettext(
                                    'The integration user for {{serviceTitle}} is no longer valid. Please re-authenticate the integration.',
                                    {
                                        serviceTitle
                                    }
                                )
                            }
                            <Link
                                className='link-btn'
                                to={`/settings/${serviceTitle.toLowerCase()}?advertisable=${advertisableEid}`}
                                openNewWindow
                                rel='noopener noreferrer'
                            >
                                <button type='button' className='btn btn-default inline-alert-btn'>
                                    {i18n.gettext('Go to Settings')}
                                </button>
                            </Link>
                        </span>
                    </>
                )}
            />
        );
    }

    /**
     * Alert when required scopes are missing.
     */
    if (hasAuthenticationGranted && missingScopes) {
        return (
            <InlineAlert
                showIcon
                type={i18n.noop.gettext('info')}
                contentEl={(
                    <>
                        <span className='ar-alert-title' data-testid='service-missing-scopes'>
                            {
                                i18n.gettext(
                                    'This action requires your permission to access "{{serviceTitle}}: {{entityTitle}}".',
                                    {
                                        serviceTitle,
                                        entityTitle,
                                    }
                                )
                            }
                        </span>
                        <span className='ar-alert-message'>
                            <Link
                                className='link-btn'
                                to={`/settings/${serviceTitle.toLowerCase()}?advertisable=${advertisableEid}`}
                                openNewWindow
                                rel='noopener noreferrer'
                            >
                                <button type='button' onClick={handleGoToSettings} className='btn btn-default inline-alert-btn'>
                                    {i18n.gettext('Go to Settings')}
                                </button>
                            </Link>
                        </span>
                    </>
                )}
            />
        );
    }

    /**
     * Alert when the service auth is missing, including the redirect button.
     */
    return (
        <InlineAlert
            showIcon
            type={i18n.noop.gettext('info')}
            contentEl={(
                <>
                    <span className='ar-alert-title'>
                        {
                            i18n.gettext(
                                'This action requires an integration with {{serviceTitle}}.',
                                {
                                    serviceTitle
                                }
                            )
                        }
                    </span>
                    <span className='ar-alert-message'>
                        {
                            i18n.gettext(
                                'Set up the {{serviceTitle}} integration.',
                                {
                                    serviceTitle
                                }
                            )
                        }
                        <Link
                            className='link-btn'
                            to={`/settings/${serviceTitle.toLowerCase()}?advertisable=${advertisableEid}`}
                            openNewWindow
                            rel='noopener noreferrer'
                        >
                            <button type='button' onClick={handleGoToSettings} className='btn btn-default inline-alert-btn'>
                                {i18n.gettext('Go to Settings')}
                            </button>
                        </Link>
                    </span>
                </>
            )}
        />
    );
};
